/**
 * Shared onboarding Zod contract (Plan 01-11).
 *
 * Single source of truth for client + server validation on the 4-step
 * first-run wizard. apps/web Zod-validates each step before POSTing;
 * apps/api Zod-validates every incoming payload before touching the DB.
 *
 * Safety-critical note (PROJECT.md): `OnboardingMember.allergies.confirmed`
 * is a `z.literal(true)` — the admin MUST tick the confirmation checkbox
 * before any member row is written. This is the single most important
 * safety mechanism in Phase 1 (severe nut + egg allergies in the seed
 * household).
 */
import { z } from 'zod';
import { Password, Username } from './auth';

// ─── Member colour-token slots (UI-SPEC § Colour — Member tokens) ──────────
export const COLOR_SLOTS = [
  'm-terracotta',
  'm-berry',
  'm-ochre',
  'm-sage',
  'm-sky',
  'm-plum',
] as const;
export type ColorSlot = (typeof COLOR_SLOTS)[number];

// ─── Allergy chips (UI-SPEC §5 fixed order, safety-critical) ───────────────
export const ALLERGY_CHIPS = [
  'nuts',
  'egg',
  'dairy',
  'gluten',
  'soy',
  'shellfish',
  'fish',
  'celery',
] as const;
export type AllergyChip = (typeof ALLERGY_CHIPS)[number];

// ─── Dietary chips (non-safety-critical; no confirmation checkbox) ─────────
export const DIETARY_CHIPS = [
  'low_fodmap',
  'vegetarian',
  'vegan',
  'halal',
  'kosher',
] as const;
export type DietaryChip = (typeof DIETARY_CHIPS)[number];

// ─── Norway coordinate range (UI-SPEC §5) ──────────────────────────────────
// Client-sent precision may be 5+ decimals; server coarsens to 2 via
// apps/api/src/util/coarsen.ts before persistence (HIGH-7).
export const HouseholdLat = z.number().gte(57.5).lte(71.5);
export const HouseholdLon = z.number().gte(4.0).lte(31.5);

/** Familienavnet, satt av systemadminen når familien opprettes (FF-11, #179). */
export const FamilyName = z.string().trim().min(1).max(40);

// ─── OnboardingMember — step 3 per-member payload ──────────────────────────
// Birth date regex matches ISO YYYY-MM-DD; refinement rejects future dates.
// `short_name` maps to members.short_name column (schema truth, not
// 'nickname'). Kept optional — defaulted to first token of `name` server-side
// when omitted.
export const OnboardingMember = z
  .object({
    name: z.string().trim().min(1).max(60),
    shortName: z.string().trim().max(40).optional().nullable(),
    role: z.enum(['admin', 'teen', 'child']),
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
      .refine((s) => new Date(s) <= new Date(), 'Birth date cannot be in the future'),
    colorSlot: z.enum(COLOR_SLOTS),
    // #139: brukernavn + passord er innloggingen. Uten `username` lager
    // serveren et fra kortnavnet (samme generator som backfillen). Uten
    // `password` kan medlemmet ikke logge inn før en admin sender en lenke for
    // nytt passord — unntatt en admin, som må ha passord (#143,
    // 400 `password_required`). PIN er valgfri hurtigbytte på kjøkkenskjermen.
    username: Username.optional(),
    password: Password.optional(),
    passwordConfirm: z.string().optional(),
    pin: z.string().regex(/^\d{4,8}$/, 'PIN must be 4-8 digits').optional(),
    pinConfirm: z.string().optional(),
    allergies: z.object({
      chips: z.array(z.enum(ALLERGY_CHIPS)).default([]),
      custom: z.array(z.string().trim().min(1).max(40)).default([]),
      // Merge-blocking safety gate (D-02 / PROJECT.md).
      confirmed: z.literal(true),
    }),
    dietary: z.object({
      chips: z.array(z.enum(DIETARY_CHIPS)).default([]),
      custom: z.array(z.string().trim().min(1).max(40)).default([]),
    }),
    notes: z.string().trim().max(400).optional().nullable(),
  })
  .refine((v) => v.pin === v.pinConfirm, {
    message: 'PIN confirmation must match',
    path: ['pinConfirm'],
  })
  .refine((v) => v.password === v.passwordConfirm, {
    message: 'password_mismatch',
    path: ['passwordConfirm'],
  });
export type OnboardingMemberInput = z.infer<typeof OnboardingMember>;

// ─── Oppsettet per familie (FF-11, #179) ───────────────────────────────────
// Systemadminen oppretter familien (navn, kode, hjemsted) og den første
// adminen (navn, brukernavn). Adminen velger passord via invitasjonen, logger
// inn med familiekoden og fullfører oppsettet her: først «Om deg» (egen
// fødselsdato, farge og egne allergier — bekreftet, som for alle andre), så
// medlemmene, så ferdig. Alt krever adminens økt; ingen token.

// GET /api/onboarding/status
export interface OnboardingStatus {
  /** Familien er opprettet av systemadminen og ikke ferdig satt opp. */
  needsSetup: boolean;
  familyName: string;
  familyCode: string;
  /** Adminen har lagret «Om deg» (egne allergier bekreftet). */
  profileConfirmed: boolean;
}

// POST /api/onboarding/me — adminens egen profil.
export const OnboardingProfile = z.object({
  shortName: z.string().trim().max(40).optional().nullable(),
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
    .refine((s) => new Date(s) <= new Date(), 'Birth date cannot be in the future'),
  colorSlot: z.enum(COLOR_SLOTS),
  allergies: z.object({
    chips: z.array(z.enum(ALLERGY_CHIPS)).default([]),
    custom: z.array(z.string().trim().min(1).max(40)).default([]),
    // Samme sikkerhetsport som for alle andre medlemmer (D-02).
    confirmed: z.literal(true),
  }),
  dietary: z.object({
    chips: z.array(z.enum(DIETARY_CHIPS)).default([]),
    custom: z.array(z.string().trim().min(1).max(40)).default([]),
  }),
  notes: z.string().trim().max(400).optional().nullable(),
});
export type OnboardingProfileInput = z.infer<typeof OnboardingProfile>;
